/**
 * Shared form components for combining analysis tables (join / union).
 *
 * These components are used by both CombineDataFramesView (the main-view flow
 * for replacing an existing step) and CombineTablesSourceRightSidebar (the
 * right-sidebar flow for adding a new source step). They are deliberately
 * state-management-agnostic — callers pass callbacks rather than owning state
 * — so they work with both global state and local useState.
 *
 * Exports:
 *  - combineTablesFormStyles: shared Aphrodite styles for table selectors
 *  - computeUpdatedTablesState: pure utility to update form state when tables change
 *  - useUnionColumnState: hook managing union column selection state
 *  - PairedTableSelector: side-by-side left/right table selector
 *  - ConfigureCombineMethodContent: join/union configuration UI (join type,
 *    join keys, or column auto-mapper)
 */
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {
  AutoMapperColumnJoinType,
  AutoMapperMode,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import DataFrameColumnAutoMapper from 'analysis-tool/data-frame-creation/column-mapping/DataFrameColumnAutoMapper';
import {CombineDataFramesState} from 'analysis-tool/data-frame-creation/combine-data-frames/CombineDataFramesState';
import AnalysisViewSelector from 'analysis-tool/ui/components/AnalysisViewSelector';
import DataFrameColumnRelationshipMultiSelector from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipMultiSelector';
import {DataFrameColumnRelationship} from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipSelector';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {JoinType} from 'pipelines/joins/JoinConstants';
import JoinTypeSelector from 'pipelines/joins/JoinTypeSelector';
import {PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView} from 'pipelines/PipelineViewUtils';
import {FONT_PROXIMA_12, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

export const combineTablesFormStyles = StyleSheet.create({
  autoMapperWrapper: {
    marginTop: 20,
    marginBottom: 20,
  },
  bannerWrapper: {
    marginTop: 20,
  },
  subsectionTitle: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    margin: '10px 0px',
    ':nth-child(1)': {
      marginTop: 0,
    },
  },
  tableSelector: {
    width: 'calc(50% - 12px)',
  },
  tableSelectorLeft: {
    marginRight: 24,
  },
  tableSelectorWrapper: {
    display: 'inline-flex',
    width: '100%',
  },
});

// ---------------------------------------------------------------------------
// Pure utilities
// ---------------------------------------------------------------------------

/**
 * Given the previous form state and new left/right table selections, returns
 * the updated state with relationships cleared (when tables changed) and the
 * data frame name auto-populated from the table names.
 */
export function computeUpdatedTablesState(
  prev: CombineDataFramesState,
  leftTable: PipelineViewGraphQL | null,
  rightTable: PipelineViewGraphQL | null
): CombineDataFramesState {
  const leftName =
    leftTable != null ? getOutputDataForView(leftTable, PipelineStepDataKind.DATASET).name : null;
  const rightName =
    rightTable != null ? getOutputDataForView(rightTable, PipelineStepDataKind.DATASET).name : null;

  return {
    ...prev,
    leftTable,
    rightTable,
    // We intentionally only check by reference here, as the references won't
    // change unless the analysis is updated.
    relationships: leftTable === prev.leftTable && rightTable === prev.rightTable ? prev.relationships : [],
    dataFrameName:
      prev.dataFrameNameHasUserEdits || leftName == null || rightName == null
        ? prev.dataFrameName
        : `${leftName} | ${rightName}`,
  };
}

// ---------------------------------------------------------------------------
// Union column state hook
// ---------------------------------------------------------------------------

export interface UnionColumnState {
  selectedPrimaryColumns: Array<DataFrameColumnDef | null>;
  setSelectedPrimaryColumns: React.Dispatch<React.SetStateAction<Array<DataFrameColumnDef | null>>>;
  selectedSecondaryColumns: Array<DataFrameColumnDef | null>;
  setSelectedSecondaryColumns: React.Dispatch<React.SetStateAction<Array<DataFrameColumnDef | null>>>;
  isManuallyChanged: boolean;
  setIsManuallyChanged: React.Dispatch<React.SetStateAction<boolean>>;
  hasDuplicateColumns: boolean;
}

/**
 * Manages the local state for union column selection (primary/secondary columns,
 * manual-change tracking, and duplicate detection).
 */
export function useUnionColumnState(): UnionColumnState {
  const [selectedPrimaryColumns, setSelectedPrimaryColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [selectedSecondaryColumns, setSelectedSecondaryColumns] = useState<Array<DataFrameColumnDef | null>>(
    []
  );
  const [isManuallyChanged, setIsManuallyChanged] = useState(false);

  const hasDuplicateColumns =
    new Set(selectedPrimaryColumns).size !== selectedPrimaryColumns.length ||
    new Set(selectedSecondaryColumns).size !== selectedSecondaryColumns.length;

  return {
    selectedPrimaryColumns,
    setSelectedPrimaryColumns,
    selectedSecondaryColumns,
    setSelectedSecondaryColumns,
    isManuallyChanged,
    setIsManuallyChanged,
    hasDuplicateColumns,
  };
}

// ---------------------------------------------------------------------------
// PairedTableSelector
// ---------------------------------------------------------------------------

export function PairedTableSelector({
  analysis,
  state,
  onUpdateTables,
  dataTestComponent,
}: {
  analysis: AnalysisGraphQL;
  state: CombineDataFramesState;
  onUpdateTables: (leftTable: PipelineViewGraphQL | null, rightTable: PipelineViewGraphQL | null) => void;
  dataTestComponent: string;
}): JSX.Element {
  return (
    <div className={css(combineTablesFormStyles.tableSelectorWrapper)}>
      <div className={css(combineTablesFormStyles.tableSelector, combineTablesFormStyles.tableSelectorLeft)}>
        <AnalysisViewSelector
          analysis={analysis}
          topLevelOnly={false}
          selectedView={state.leftTable}
          onSelect={(view) => {
            onUpdateTables(view, state.rightTable);
          }}
          dataKind={PipelineStepDataKind.DATASET}
          dataTestAttrs={{component: dataTestComponent, element: 'leftTableSelector'}}
        />
      </div>
      <div className={css(combineTablesFormStyles.tableSelector)}>
        <AnalysisViewSelector
          analysis={analysis}
          topLevelOnly={false}
          selectedView={state.rightTable}
          onSelect={(view) => {
            onUpdateTables(state.leftTable, view);
          }}
          dataKind={PipelineStepDataKind.DATASET}
          dataTestAttrs={{component: dataTestComponent, element: 'rightTableSelector'}}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfigureCombineMethodContent
// ---------------------------------------------------------------------------

/**
 * Renders the "Configure combine method" stepper step content, shared between
 * CombineDataFramesView and CombineTablesSourceRightSidebar.
 */
export function ConfigureCombineMethodContent({
  state,
  onUpdateJoinType,
  onUpdateRelationships,
  unionColumnState,
}: {
  state: CombineDataFramesState;
  onUpdateJoinType: (joinType: JoinType) => void;
  onUpdateRelationships: (relationships: Array<DataFrameColumnRelationship>) => void;
  unionColumnState: UnionColumnState;
}): JSX.Element {
  const handleUnionRelationshipChange = (relationships: Array<DataFrameColumnRelationship>): void => {
    unionColumnState.setSelectedPrimaryColumns(
      relationships.map((relationship) =>
        state.leftTable != null
          ? (getOutputDataForView(state.leftTable, PipelineStepDataKind.DATASET).columns.find(
              (column) => column.systemName === relationship.leftColumn
            ) ?? null)
          : null
      )
    );
    unionColumnState.setSelectedSecondaryColumns(
      relationships.map((relationship) =>
        state.rightTable != null
          ? (getOutputDataForView(state.rightTable, PipelineStepDataKind.DATASET).columns.find(
              (column) => column.systemName === relationship.rightColumn
            ) ?? null)
          : null
      )
    );
    unionColumnState.setIsManuallyChanged(true);
  };

  return (
    <>
      {state.combineMethod === PipelineStepType.JOIN && (
        <>
          <div className={css(combineTablesFormStyles.subsectionTitle)}>Select a join type</div>
          <JoinTypeSelector joinType={state.joinType} onSelectJoinType={onUpdateJoinType} />
        </>
      )}
      {state.combineMethod === PipelineStepType.JOIN && state.joinType !== 'POSITIONAL' && (
        <>
          <div className={css(combineTablesFormStyles.subsectionTitle)}>Specify join keys</div>
          {state.leftTable && state.rightTable ? (
            <DataFrameColumnRelationshipMultiSelector
              addItemLabel="Add a join key"
              leftDataFrame={getOutputDataForView(state.leftTable, PipelineStepDataKind.DATASET)}
              rightDataFrame={getOutputDataForView(state.rightTable, PipelineStepDataKind.DATASET)}
              relationships={state.relationships}
              onSelectRelationships={onUpdateRelationships}
            />
          ) : (
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="Please select tables to combine first."
            />
          )}
        </>
      )}
      {state.combineMethod === PipelineStepType.UNION_DATASETS && (
        <>
          <div className={css(combineTablesFormStyles.subsectionTitle)}>Map columns to union</div>
          The union table will inherit column header names from Analysis table 1.
          {state.leftTable && state.rightTable ? (
            <>
              {unionColumnState.hasDuplicateColumns && (
                <div className={css(combineTablesFormStyles.bannerWrapper)}>
                  <InlineBanner
                    bannerType={InlineBannerType.ERROR}
                    text="Each column can only be selected once"
                  />
                </div>
              )}
              <div className={css(combineTablesFormStyles.autoMapperWrapper)}>
                <DataFrameColumnAutoMapper
                  templateColumns={
                    getOutputDataForView(state.leftTable, PipelineStepDataKind.DATASET).columns
                  }
                  aligningColumns={
                    getOutputDataForView(state.rightTable, PipelineStepDataKind.DATASET).columns
                  }
                  setSelectedTemplateColumns={unionColumnState.setSelectedPrimaryColumns}
                  setSelectedAligningColumns={unionColumnState.setSelectedSecondaryColumns}
                  initialAutoMapperMode={AutoMapperMode.NAME}
                  initialAutoMapperColumnJoinType={AutoMapperColumnJoinType.KEEP_SHARED}
                  allowModeChange={true}
                  allowColumnJoinTypeChange={false}
                  autoMap={!unionColumnState.isManuallyChanged}
                />
              </div>
              <DataFrameColumnRelationshipMultiSelector
                addItemLabel="Add a column pair"
                leftDataFrame={getOutputDataForView(state.leftTable, PipelineStepDataKind.DATASET)}
                rightDataFrame={getOutputDataForView(state.rightTable, PipelineStepDataKind.DATASET)}
                relationships={unionColumnState.selectedPrimaryColumns.map(
                  (selectedColumn, index): DataFrameColumnRelationship => ({
                    leftColumn: selectedColumn?.systemName ?? null,
                    rightColumn: unionColumnState.selectedSecondaryColumns[index]?.systemName ?? null,
                  })
                )}
                onSelectRelationships={handleUnionRelationshipChange}
              />
            </>
          ) : (
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="Please select tables to combine first."
            />
          )}
        </>
      )}
    </>
  );
}
